import React, { useState, useEffect, useRef } from "react";
import { fabric } from "fabric";

import General from "../utils/General";
import Backend from "../utils/Backend";

import TopBar from "./components/TopBar";
import Editor from "./components/editor/Editor";

import FilterEditorModal from "./components/modal/FilterEditorModal";

import ToolBar from "./components/ToolBar";
import Cropper from "./components/editor/Cropper";
import Retouch from "./components/editor/Retouch";

import useUndoRedo from "./components/useUndoRedo";

const history = [];
const IMAGE_BASE_URL = "https://d2db4y98rlegpn.cloudfront.net/images/customers";
const IMAGE = { original: `${IMAGE_BASE_URL}/image00007.jpeg` };

const PhotoEdit = (props) => {
  const {
    state,
    setState,
    undo,
    redo,
    isUndoPossible,
    isRedoPossible,
    pastStates,
    futureStates,
  } = useUndoRedo(null);

  const canvasRef = useRef(null);
  const cropperRef = useRef(null);

  const [data, setData] = useState(null);
  const [selectedTextColor, setSelectedTextColor] = useState(null);
  const [project, setProject] = useState(null);
  const [historyStep, setHistoryStep] = useState(null);
  const [inApp, setInApp] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showFilterEditor, setShowFilterEditor] = useState(false);
  const [showHSL, setShowHSL] = useState(false);
  // const [logs, setLogs] = useState("");
  // var mLogs = "";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let inApp = params.get("inApp");
    const projectId = params.get("id");

    window.onmessage = (event) => {
      handleEvent(event);
    };

    // Android sends via document: https://github.com/react-native-webview/react-native-webview/issues/2980#issue-1737934676
    document.addEventListener("message", (event) => {
      handleEvent(event);
    });

    if (projectId) {
      setupProject(projectId);
    } else {
      var textureLimit = fabric.textureSize;
      if (fabric.isWebglSupported()) {
        textureLimit = fabric.maxTextureSize;
      }

      let canvasLimit = getMaxCanvasSize().width;
      let maxLimit = Math.min(textureLimit, canvasLimit);

      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage("ready");
        window.ReactNativeWebView.postMessage(`maxLimit:${maxLimit}`);
      }
    }

    if (inApp == null) {
      inApp = window.ReactNativeWebView != null;
    } else {
      inApp = inApp === "true";
    }

    setInApp(inApp);

    // const originalLog = console.log;
    // console.log = function (...args) {
    //   mLogs = JSON.stringify(args) + "\n" + mLogs;
    //   setLogs(mLogs);

    //   // Call the original console.log with all arguments
    //   originalLog.apply(console, args);
    // };

    // const originalError = console.error;
    // console.error = function (...args) {
    //   mLogs =
    //     "Error: ============" + "\n" + JSON.stringify(args) + "\n" + mLogs;

    //   setLogs(mLogs);
    //   // Call the original console.log with all arguments
    //   originalError.apply(console, args);
    // };

    console.log("Project id: " + projectId);
  }, []);

  // useEffect(() => {
  //   mLogs += logs;
  // }, [logs]);

  useEffect(() => {
    if (!isSaving) {
      setIsUploading(false);
    }
  }, [isSaving]);

  useEffect(() => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(`isUploading:${isUploading}`);
    }
  }, [isUploading]);

  function getMaxCanvasSize() {
    // Create a temporary canvas element
    const tempCanvas = document.createElement("canvas");
    const context = tempCanvas.getContext("2d");

    let width = 2048;
    let height = 2048;
    const maxLimit = 4096; // Hard maximum limit
    const incrementalSize = 1024;

    while (width <= maxLimit && height <= maxLimit) {
      try {
        // Set canvas size
        tempCanvas.width = width;
        tempCanvas.height = height;

        // Clear and fill the canvas with a test color
        context.clearRect(0, 0, width, height);
        context.fillStyle = "rgb(255, 0, 0)"; // Red color
        context.fillRect(0, 0, 1, 1); // Draw a small rectangle

        // Read the pixel back to verify if drawing worked
        const pixelData = context.getImageData(0, 0, 1, 1).data;

        // Check if the pixel color is what we set (255, 0, 0)
        if (pixelData[0] !== 255 || pixelData[1] !== 0 || pixelData[2] !== 0) {
          throw new Error("Rendering failed");
        }

        // Increment dimensions
        width += incrementalSize;
        height += incrementalSize;
      } catch (e) {
        // If rendering fails, return the last successful size
        console.log(
          `Max Canvas Size Reached: ${width - incrementalSize} x ${
            height - incrementalSize
          }`
        );
        return {
          width: width - incrementalSize,
          height: height - incrementalSize,
        };
      }
    }

    // If we exit the loop without errors, return the final size
    // console.log(
    //   `Max Canvas Size within limit: ${width - incrementalSize} x ${
    //     height - incrementalSize
    //   }`
    // );
    return { width: width - incrementalSize, height: height - incrementalSize };
  }

  useEffect(() => {
    if (
      project?.id &&
      project?.current_state?.preview_image?.id !== state?.preview_image?.id
    ) {
      saveProject();
    }
  }, [state?.preview_image?.id]);

  const saveProject = () => {
    if (!project) {
      console.log("success: project");
      return;
    }

    setIsSaving(true);
    console.log("success: setIsSaving");
    let data = {
      state: {
        data: state,
        order: state.order,
        tool: state.currentTab,
        filter: state.filter.id,
        preview_image: state.preview_image?.id,
        retouched_image: state.retouched_image,
      },
    };

    Backend.updateProject(project, data)
      .then(() => {
        console.log("success: updateProject");
        setIsSaving(false);

        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage("project-saved");
        }
      })
      .catch((err) => {
        console.log("catch: err", err);
        setIsSaving(false);
        console.log("err", err);
      });
  };

  const setupProject = (id) => {
    Backend.getProject(id)
      .then((project) => {
        let projectState = getState(project);

        projectState.retouched_image = project.current_state?.retouched_image;

        updateState({ ...projectState });

        setProject(project);

        // if (window.ReactNativeWebView) {
        //   window.ReactNativeWebView.postMessage("project-loading");
        // }
      })
      .catch((err) => {
        console.log("err", err);
      });
  };

  const handleEvent = (event) => {
    try {
      let data = JSON.parse(event.data);
      console.log(data.type);
      if (data?.type == "create_project") {
        setupProjectFromImageData(data);
      }
      if (data?.type == "image_saved") {
        updateState({ preview_image: data.image });
      }
    } catch (error) {}
  };

  const setupProjectFromImageData = (fileData) => {
    if (data) {
      return;
    }

    let project = {
      original_image: {
        original: fileData.base64_image,
        width: fileData.metadata.width,
        height: fileData.metadata.height,
      },
    };

    let projectState = getState(project);

    project.original_image.thumbnail = fileData.base64_thumbnail;

    updateState({ ...projectState });
    // setProject(project); // this makes the web app crash due to loading huge base64 in memory.

    setIsSaving(true);

    // if (window.ReactNativeWebView) {
    //   window.ReactNativeWebView.postMessage("project-loading");
    // }

    let file = General.dataURIToBlob(fileData.base64_image, "image/jpeg");
    Backend.uploadImage(file, fileData.metadata, (progress) => {
      setUploadProgress(progress);
    })
      .then((image) => {
        let projectData = {
          type: "photo",
          name: fileData.metadata.title,
          original_image: image.id,
          state: {
            data: {
              ...projectState,
              image,
              originalImage: image,
              preview_image: image,
            },
            order: 0,
            preview_image: image.id,
            filter: projectState.filter.id,
          },
        };

        // setting manually to avoid triggering history
        setData((current) => {
          let newData = { ...current };
          newData.image = image;
          newData.originalImage = image;
          newData.preview_image = image;
          setState(newData);
          return newData;
        });

        return Backend.createProject(projectData);
      })
      .then((project) => {
        setIsSaving(false);
        setProject(project);
      })
      .catch((error) => {
        console.log("error", error);
      });
  };

  const getSize = (image) => {
    let maxSize = 2048;

    if (image.width <= maxSize && image.height <= maxSize) {
      return { width: image.width, height: image.height };
    }

    if (image.width > image.height) {
      let newWidth = maxSize;

      let ratio = image.width / maxSize;

      let newHeight = Math.round(image.height / ratio);

      return { width: newWidth, height: newHeight };
    }

    let newHeight = maxSize;

    let ratio = image.height / maxSize;

    let newWidth = Math.round(image.width / ratio);

    return { width: newWidth, height: newHeight };
  };

  const getState = (project) => {
    if (project.current_state?.data?.image) {
      return project.current_state?.data;
    }

    let image = project.original_image || IMAGE;

    // if (image.thumbnail) {
    //   image.original = image.thumbnail;

    //   let size = getSize(image);

    //   image.original = image.original.replace(
    //     "400x225",
    //     `${size.width}x${size.height}`
    //   );
    // }

    return {
      image,
      originalImage: image,
      adjustments: {
        brightness: 0,
        contrast: 0,
        saturation: 0,
        exposure: 0,
        highlights: 1,
        hsl: {
          hue: 0,
          saturation: 0,
          lightness: 0,
        },
      },
      filter: {
        id: 1,
        type: "Lightroom",
        key: "none",
        name: "None",
        intensity: 1.0,
        data: {
          type: "Lightroom",
          name: "None",
        },
      },
      text: {
        value: null,
        size: "150",
        color: "black",
        backgroundColor: "white",
        font: "Ruda",
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        scaleX: 1,
        scaleY: 1,
      },
      frame: "none",
      transform: {
        rotation: 0,
        flipX: false,
      },
      aspectRatio: null,
      crop: null,
      currentTab: "filter",
      order: 0,
    };
  };

  const updateHistory = useRef(
    General.debounce(
      (state) => {
        setState(state);
      },
      1000,
      false
    )
  ).current;

  const onFilterEdited = useRef(
    General.debounce(
      (filter) => {
        if (filter.data.intensity == 1 || filter.data.intensity == 0.8) {
          return;
        }
        Backend.addEditFilter({ ...filter });
      },
      1000,
      false
    )
  ).current;

  const updateState = (newData) => {
    setData((current) => {
      let updatedData = {
        ...current,
        ...newData,
        order: state?.order ? state.order + 1 : 1,
      };
      updateHistory(updatedData);
      return updatedData;
    });
  };

  const onUndoClicked = () => {
    setData(pastStates[pastStates.length - 1]);

    undo();
    setTimeout(() => {
      setEditorKey((prevKey) => prevKey + 1);
    }, 300);
  };

  const onRedoClicked = () => {
    setData(futureStates[0]);

    redo();
    setTimeout(() => {
      setEditorKey((prevKey) => prevKey + 1);
    }, 300);
  };

  const onApplyClicked = () => {
    if (cropperRef.current) {
      cropperRef.current.applyChanges();
    }
    // TODO: trigger applyChanges from Cropper.js here
  };
  const handleTextChange = General.debounce(
    (text) => {
      updateState({ text });
    },
    300,
    false
  );

  const handleSave = () => {
    if (canvasRef.current) {
      if (isSaving) {
        return;
      }

      setUploadProgress(0);
      setIsUploading(true);
      canvasRef.current.saveImage();
    }
  };
  const [editorKey, setEditorKey] = React.useState(0);

  const renderPreview = () => {
    if (!data || !project) {
      return;
    }

    let retouchedImage = data.retouched_image || data.image;

    return (
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <Editor
            ref={canvasRef}
            state={data}
            image={retouchedImage}
            // key={data.retouched_image?.id}
            key={editorKey}
            onTextChange={(text) => handleTextChange(text)}
            onImageSaved={(image) => {
              updateState({ preview_image: image });
            }}
            onColorSelected={(color) => {
              data.text.color = color;
              setData(data);
            }}
          />
        </div>

        {data.currentTab === "retouch" && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <Retouch
              state={data}
              image={retouchedImage}
              onUpdated={(state) => {
                updateState({ ...state, tool: data.currentTab });
                setEditorKey((prevKey) => prevKey + 1);
              }}
            />
          </div>
        )}
        {(data.currentTab === "straighten" || data.currentTab === "crop") && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <Cropper
              ref={cropperRef}
              // key={retouchedImage.original}
              key={editorKey}
              state={data}
              image={retouchedImage}
              onUpdated={(state) => {
                updateState(state);
                setTimeout(() => {
                  setEditorKey((prevKey) => prevKey + 1);
                }, 300);
              }}
            />
          </div>
        )}
      </div>
    );
  };

  if (!data) {
    return null;
  }

  let pageCSS = "pages photo-edit ";

  // Straighten, Rotate
  return (
    <div className={pageCSS}>
      <TopBar
        onSaveClicked={() => {
          setIsSaving(true);
          handleSave();
        }}
        isSaving={isSaving}
        addToPlanner={() => {}}
        currentTab={data.currentTab}
        progress={uploadProgress.toFixed(0)}
      />
      <div
        id="previewContainer"
        className="d-flex flex-column justify-content-between main"
      >
        {renderPreview()}
        <ToolBar
          activeTab={data.currentTab || "filter"}
          state={data}
          aspectRatio={data.aspectRatio}
          onStateUpdated={(state, action) => {
            updateState(state);
          }}
          onTabChanged={(currentTab) => {
            updateState({ currentTab });
          }}
          onFilterUpdated={(filter) => {
            updateState({ filter, tool: "filter" });
            onFilterEdited(filter);
          }}
          onAspectRatioChanged={(aspectRatio) => {
            updateState({ aspectRatio, tool: "" });
          }}
          onTextUpdated={(text) => {
            updateState({ text, tool: "text" });
          }}
          onFrameUpdated={(frame) => {
            updateState({ frame, tool: "frame" });
          }}
          onTransformUpdated={(transform) => {
            updateState({ transform, tool: "adjust" });
          }}
          onFilterSettingsClicked={() => setShowFilterEditor(true)}
          showHSL={showHSL}
          setShowHSL={setShowHSL}
          onHSLClicked={() => setShowHSL(true)}
          undoDisabled={isUndoPossible}
          redoDisabled={isRedoPossible}
          onUndoClicked={onUndoClicked}
          onRedoClicked={onRedoClicked}
          onApplyClicked={onApplyClicked}
        />
      </div>
      {showFilterEditor && (
        <FilterEditorModal
          show={showFilterEditor}
          filter={data.filter}
          onHide={() => setShowFilterEditor(false)}
          onUpdated={(filter) => {
            updateState({ filter });
          }}
        />
      )}

      {/* <div
        style={{
          position: "absolute",
          width: "100%",
          height: "20%",
          backgroundColor: "#00000060",
          overflow: "scroll",
          overflowWrap: "anywhere",
          whiteSpace: "pre-line",
        }}
      >
        {logs}
      </div> */}
    </div>
  );
};

export default PhotoEdit;
